import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { propOr } from 'ramda';
import { Formik, Field } from 'formik';
import * as yup from 'yup';
import {
  TextField,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  FormControl,
  FormHelperText,
  CircularProgress,
  Typography,
} from '@mui/material';

/* API */
import { apiRequest } from 'lib/API';
import { usePostUpdateWorkSearch } from 'lib/hooks/workSearchQueries';

import { DatePicker, TimePicker } from '@mui/x-date-pickers';

/**
 * i18n
 */
import { useIntl } from 'react-intl';

/* Analytics */
import { trackEvent } from 'lib/analytics';

import styles from 'styles/apply-job-form.module.scss';
import onbStyles from 'styles/onboarding.module.scss';

import BrowseDocuments from 'components/BrowseDocuments';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const PREDEFINED_RESULT_OPTIONS = ['resumes_submitted', 'interviews_scheduled', 'performed_interview'];

function AttendJobSeminar({ setOpen, selectedActivity, editting = false, week, onSubmit }) {
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [editMode, setEditMode] = useState(true);
  const [refreshConter, setRefreshConter] = useState(0);
  const [documentsLinks, setDocumentsLinks] = useState([]);
  const [dateError, setDateError] = useState('');
  const intl = useIntl();
  const postUpdateWorkSearchMutation = usePostUpdateWorkSearch(
    editting,
    () => {
      setOpen(false);
      setSubmitting(false);
    },
    () => {
      setSubmitting(false);
      setSaveError(true);
    }
  );

  useEffect(() => {
    setRefreshConter(refreshConter + 1);
    if (selectedActivity && selectedActivity.upload_documents && selectedActivity.upload_documents.length > 0) {
      setDocumentsLinks([...selectedActivity.upload_documents]);
    } else {
      setDocumentsLinks([]);
    }
  }, [selectedActivity, editting]);

  useEffect(() => {
    setEditMode(!editting || Boolean(selectedActivity?.draft));
  }, [editting, selectedActivity?.draft, selectedActivity?.worksearch_id]);

  useEffect(() => {
    let cancelled = false;
    const documentsWithNoLinks = documentsLinks.filter((d) => !d.downloadUrl);
    if (documentsWithNoLinks.length > 0) {
      documentsWithNoLinks.forEach((doc) => {
        apiRequest(
          'get',
          `/users/me/worksearch/${encodeURIComponent(selectedActivity.worksearch_id)}?file_uuid=${encodeURIComponent(
            doc.file_uuid
          )}`
        )
          .then((res) => {
            if (!cancelled) {
              setDocumentsLinks((prev) =>
                prev.map((d) => (d.file_uuid === doc.file_uuid ? { ...d, downloadUrl: res.activity?.download_url } : d))
              );
            }
          })
          .catch(() => {});
      });
    }
    return () => {
      cancelled = true;
    };
  }, [JSON.stringify(documentsLinks)]);

  const emailRegExp = /^\S+@\S+\.\w\w+$/;

  const validationSchema = yup.object({
    job_fair: yup
      .string(intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' }))
      .required(intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' })),
    job_fair_website: yup.string(intl.formatMessage({ id: 'jobSearchActivity.fair.urlRequired' })).matches(
      /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/, //eslint-disable-line
      { message: intl.formatMessage({ id: 'jobSearchActivity.fair.urlRequired' }), excludeEmptyString: true }
    ),
    contact_email: yup.string(intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' })).matches(emailRegExp, {
      message: intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' }),
      excludeEmptyString: true,
    }),
    date_of_job_fair: yup
      .string(intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' }))
      .required(intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' })),
    time_of_job_fair: yup
      .string(intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' }))
      .required(intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' })),
    location: yup
      .string(intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' }))
      .required(intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' })),
    result_of_job_fair: yup.array().min(1, intl.formatMessage({ id: 'forms.onboarding.minSelected' }, { length: 1 })),
  });

  const toggleCheckbox = (currentValues, key) => {
    const valuesSet = new Set(currentValues);
    if (valuesSet.has(key)) {
      valuesSet.delete(key);
    } else {
      valuesSet.add(key);
    }
    return [...valuesSet];
  };

  const defaultValues = {
    job_fair: '',
    job_fair_website: '',
    contact_email: '',
    date_of_job_fair: null,
    time_of_job_fair: null,
    location: '',
    result_of_job_fair: '',
    result_of_job_fair_other: '',
    notes: '',
    upload_documents: [],
    draft: false,
  };

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={validationSchema}
      initialValues={
        selectedActivity
          ? (() => {
              const savedResults = Array.isArray(selectedActivity.result_of_job_fair)
                ? selectedActivity.result_of_job_fair
                : [];
              const predefined = savedResults.filter((v) => PREDEFINED_RESULT_OPTIONS.includes(v));
              const otherText = savedResults.find((v) => !PREDEFINED_RESULT_OPTIONS.includes(v) && v !== 'other') || '';
              return {
                ...selectedActivity,
                job_fair: propOr('', 'job_fair', selectedActivity),
                job_fair_website: propOr('', 'job_fair_website', selectedActivity),
                contact_email: propOr('', 'contact_email', selectedActivity),
                location: propOr('', 'location', selectedActivity),
                notes: propOr('', 'notes', selectedActivity),
                result_of_job_fair: otherText ? [...predefined, 'other'] : predefined,
                result_of_job_fair_other: otherText,
                upload_documents: [],
                time_of_job_fair: new Date(selectedActivity.time_of_job_fair),
                date_of_job_fair: new Date(
                  selectedActivity.date_of_job_fair.slice(0, 4),
                  selectedActivity.date_of_job_fair.slice(5, 7) - 1,
                  selectedActivity.date_of_job_fair.slice(8, 10)
                ),
              };
            })()
          : defaultValues
      }
      onSubmit={async ({ result_of_job_fair_other, result_of_job_fair, ...values }) => {
        setSubmitting(true);
        setSaveError(false);
        let year = values.date_of_job_fair.getFullYear();
        let month = values.date_of_job_fair.getMonth() + 1;
        let day = values.date_of_job_fair.getDate();
        if (month < 10) month = '0' + month;
        if (day < 10) day = '0' + day;
        const resultVal = result_of_job_fair.includes('other')
          ? result_of_job_fair.filter((v) => v != 'other').concat(result_of_job_fair_other)
          : result_of_job_fair;

        const vals4submit = {
          ...values,
          result_of_job_fair: resultVal,
          activity_type: 'attend_job_fair',
          activity_date: `${year}-${month}-${day}`,
          date_of_job_fair: `${year}-${month}-${day}`,
        };
        vals4submit.upload_documents = [
          ...documentsLinks.map((doc) => ({ file_uuid: doc.file_uuid, filename: doc.filename })),
          ...values.upload_documents.map((doc) => ({ filename: doc.name, upload_contents: doc.contents })),
        ];
        if (onSubmit && typeof onSubmit === 'function') {
          return onSubmit(vals4submit);
        }
        if (editting) {
          trackEvent('WEEK_VIEWACTIVITY_SAVE');
        } else {
          trackEvent('WEEK_SAVEACTIVITY_JOBFAIR');
        }
        postUpdateWorkSearchMutation.mutate(vals4submit);
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, touched, values, setFieldValue }) => {
        function isValidDate(d) {
          return d instanceof Date && !isNaN(d);
        }
        const handleDateChange = (date) => {
          const minDate = new Date(week.from.slice(0, 4), week.from.slice(5, 7) - 1, week.from.slice(8, 10));
          const maxDate = new Date(week.to.slice(0, 4), week.to.slice(5, 7) - 1, week.to.slice(8, 10));
          if (isValidDate(date)) {
            setFieldValue('date_of_job_fair', date);
            if (date < minDate || date > maxDate) {
              setDateError('The selected date is outside the valid range');
            } else {
              setDateError('');
            }
          } else {
            let error = intl.formatMessage({ id: 'jobSearchActivity.validation.invalidDateFormat' });
            if (Object.is(date, null)) {
              error = intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' });
            }
            setDateError(error);
            setFieldValue('date_of_job_fair', '');
          }
        };
        useEffect(() => {
          if (touched.date_of_job_fair) {
            handleDateChange(values.date_of_job_fair);
          }
        }, [touched.date_of_job_fair]);
        return (
          <form onSubmit={handleSubmit} className={styles.loginFlow__form}>
            {submitting ? (
              <Box m={2} display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            ) : (
              <>
                <div className={styles.fields}>
                  <div>
                    <TextField
                      InputLabelProps={{ required: true }}
                      disabled={submitting || (editting && !editMode)}
                      error={touched.job_fair && Boolean(errors.job_fair)}
                      fullWidth
                      helperText={touched.job_fair && errors.job_fair}
                      label={intl.formatMessage({ id: 'jobSearchActivity.fair.jobFair' })}
                      margin="normal"
                      name="job_fair"
                      classes={{ root: styles.textFieldRoot }}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type="text"
                      value={values.job_fair}
                      variant="filled"
                      inputProps={{
                        title: intl.formatMessage({ id: 'jobSearchActivity.fair.jobFair' }),
                      }}
                      id="job-fair-work-search-field"
                    />
                  </div>
                  <div>
                    <TextField
                      InputLabelProps={{ required: true }}
                      disabled={submitting || (editting && !editMode)}
                      error={touched.location && Boolean(errors.location)}
                      fullWidth
                      helperText={touched.location && errors.location}
                      label={intl.formatMessage({ id: 'jobSearchActivity.fair.locationOfJobFair' })}
                      name="location"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      classes={{ root: styles.textFieldRoot }}
                      type="text"
                      value={values.location}
                      variant="filled"
                      inputProps={{
                        title: intl.formatMessage({ id: 'jobSearchActivity.fair.locationOfJobFair' }),
                      }}
                      id="location-job-fair-work-search-field"
                    />
                  </div>
                  <Box sx={{ position: 'relative' }}>
                    <DatePicker
                      onError={(newError) => {
                        if (['minDate', 'maxDate'].indexOf(newError) > -1) {
                          setDateError(intl.formatMessage({ id: 'jobSearchActivity.form.dateOutOfRange' }));
                        } else {
                          setDateError(newError);
                        }
                      }}
                      disabled={submitting || (editting && !editMode)}
                      label={`${intl.formatMessage({ id: 'jobSearchActivity.fair.dateOfJobFair' })} *`}
                      minDate={new Date(week.from.slice(0, 4), week.from.slice(5, 7) - 1, week.from.slice(8, 10))}
                      maxDate={new Date(week.to.slice(0, 4), week.to.slice(5, 7) - 1, week.to.slice(8, 10))}
                      slotProps={{
                        textField: {
                          variant: 'filled',
                          helperText:
                            touched.date_of_job_fair && errors.date_of_job_fair ? errors.date_of_job_fair : dateError,
                          error: dateError || (touched.date_of_job_fair && Boolean(errors.date_of_job_fair)),
                          id: 'date-job-fair-work-search-field',
                        },
                      }}
                      onChange={(newValue) => {
                        setFieldValue('date_of_job_fair', newValue);
                      }}
                      value={values.date_of_job_fair}
                    />
                    <HelpOutlineIcon
                      sx={{
                        color: (theme) => theme.palette.grey['800'],
                        fontSize: 16,
                        position: 'absolute',
                        top: { xs: 61, md: 53 },
                        left: 0,
                      }}
                    />
                    <span className={cn(styles.fieldDescription, styles.fieldDescriptionSemiClose)}>
                      {intl.formatMessage({ id: 'jobSearchActivity.form.dateRange' })}
                    </span>
                  </Box>
                  <Box>
                    <TimePicker
                      disabled={submitting || (editting && !editMode)}
                      label={`${intl.formatMessage({ id: 'jobSearchActivity.fair.timeOfJobFair' })} *`}
                      slotProps={{
                        textField: {
                          variant: 'filled',
                          helperText: touched.time_of_job_fair && errors.time_of_job_fair,
                          error: touched.time_of_job_fair && Boolean(errors.time_of_job_fair),
                          id: 'time-job-fair-work-search-field',
                        },
                      }}
                      onChange={(newValue) => {
                        setFieldValue('time_of_job_fair', newValue);
                      }}
                      value={values.time_of_job_fair}
                    />
                  </Box>
                  <div>
                    <TextField
                      disabled={submitting || (editting && !editMode)}
                      error={touched.job_fair_website && Boolean(errors.job_fair_website)}
                      fullWidth
                      helperText={touched.job_fair_website && errors.job_fair_website}
                      label={intl.formatMessage({ id: 'jobSearchActivity.fair.jobFairWebsite' })}
                      margin="normal"
                      name="job_fair_website"
                      classes={{ root: styles.textFieldRoot }}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type="text"
                      value={values.job_fair_website}
                      variant="filled"
                      inputProps={{
                        title: intl.formatMessage({ id: 'jobSearchActivity.fair.jobFairWebsite' }),
                      }}
                      id="website-job-fair-work-search-field"
                    />
                  </div>
                  <div>
                    <TextField
                      disabled={submitting || (editting && !editMode)}
                      error={touched.contact_email && Boolean(errors.contact_email)}
                      fullWidth
                      helperText={touched.contact_email && errors.contact_email}
                      label={intl.formatMessage({ id: 'jobSearchActivity.form.contactEmail' })}
                      name="contact_email"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      classes={{ root: styles.textFieldRoot }}
                      type="text"
                      value={values.contact_email}
                      variant="filled"
                      inputProps={{
                        title: intl.formatMessage({ id: 'jobSearchActivity.form.contactEmail' }),
                      }}
                      id="email-job-fair-work-search-field"
                    />
                  </div>
                </div>
                <div className={styles.checksAndRadios}>
                  <div className={styles.checks}>
                    <span
                      className={cn({
                        [styles.fieldLabel]: true,
                        [styles.fieldLabel__errored]: touched.result_of_job_fair && errors.result_of_job_fair,
                      })}
                    >
                      {intl.formatMessage({ id: 'jobSearchActivity.fair.ResultOfJobFair' })}*
                    </span>
                    <span className={cn(styles.fieldDescription, styles.labelPadding)}>
                      {intl.formatMessage({ id: 'jobSearchActivity.form.allThatApply' })}
                    </span>
                    <FormControl classes={{ root: onbStyles.formControlRoot }}>
                      <FormControlLabel
                        labelPlacement="end"
                        classes={{ root: onbStyles.formControlLabelRoot, label: onbStyles.formControlLabeLabel }}
                        label={intl.formatMessage({ id: 'jobSearchActivity.fair.resultOption1' })}
                        control={
                          <Checkbox
                            className={styles.checkBoxRoot}
                            disabled={submitting || (editting && !editMode)}
                            checked={values.result_of_job_fair.includes('resumes_submitted')}
                            color="primary"
                            onChange={() =>
                              handleChange({
                                target: {
                                  name: 'result_of_job_fair',
                                  value: toggleCheckbox(values.result_of_job_fair, 'resumes_submitted'),
                                },
                              })
                            }
                            name="result_of_job_fair"
                            id="resume-job-fair-work-search-checkbox"
                          />
                        }
                      />
                    </FormControl>
                    <FormControl classes={{ root: onbStyles.formControlRoot }}>
                      <FormControlLabel
                        labelPlacement="end"
                        classes={{ root: onbStyles.formControlLabelRoot, label: onbStyles.formControlLabeLabel }}
                        label={intl.formatMessage({ id: 'jobSearchActivity.fair.resultOption2' })}
                        control={
                          <Checkbox
                            className={styles.checkBoxRoot}
                            disabled={submitting || (editting && !editMode)}
                            checked={values.result_of_job_fair.includes('interviews_scheduled')}
                            color="primary"
                            onChange={() =>
                              handleChange({
                                target: {
                                  name: 'result_of_job_fair',
                                  value: toggleCheckbox(values.result_of_job_fair, 'interviews_scheduled'),
                                },
                              })
                            }
                            name="result_of_job_fair"
                            id="interview-scheduled-job-fair-work-search-checkbox"
                          />
                        }
                      />
                    </FormControl>
                    <FormControl classes={{ root: onbStyles.formControlRoot }}>
                      <FormControlLabel
                        labelPlacement="end"
                        classes={{ root: onbStyles.formControlLabelRoot, label: onbStyles.formControlLabeLabel }}
                        label={intl.formatMessage({ id: 'jobSearchActivity.fair.resultOption3' })}
                        control={
                          <Checkbox
                            className={styles.checkBoxRoot}
                            disabled={submitting || (editting && !editMode)}
                            checked={values.result_of_job_fair.includes('performed_interview')}
                            color="primary"
                            onChange={() =>
                              handleChange({
                                target: {
                                  name: 'result_of_job_fair',
                                  value: toggleCheckbox(values.result_of_job_fair, 'performed_interview'),
                                },
                              })
                            }
                            name="result_of_job_fair"
                            id="interview-job-fair-work-search-checkbox"
                          />
                        }
                      />
                    </FormControl>
                    <FormControl classes={{ root: onbStyles.formControlRoot }}>
                      <FormControlLabel
                        labelPlacement="end"
                        classes={{ root: onbStyles.formControlLabelRoot, label: onbStyles.formControlLabeLabel }}
                        label={intl.formatMessage({ id: 'jobSearchActivity.fair.resultOption4' })}
                        control={
                          <Checkbox
                            className={styles.checkBoxRoot}
                            disabled={submitting || (editting && !editMode)}
                            checked={values.result_of_job_fair.includes('other')}
                            color="primary"
                            onChange={() =>
                              handleChange({
                                target: {
                                  name: 'result_of_job_fair',
                                  value: toggleCheckbox(values.result_of_job_fair, 'other'),
                                },
                              })
                            }
                            name="result_of_job_fair"
                            id="other-job-fair-work-search-checkbox"
                          />
                        }
                      />
                    </FormControl>
                    {values.result_of_job_fair.includes('other') ? (
                      <Box pt={1}>
                        <TextField
                          InputLabelProps={{ required: true }}
                          disabled={submitting || (editting && !editMode)}
                          error={touched.result_of_job_fair_other && Boolean(errors.result_of_job_fair_other)}
                          fullWidth
                          helperText={touched.result_of_job_fair_other && errors.result_of_job_fair_other}
                          label={intl.formatMessage({ id: 'jobSearchActivity.fair.resultOption4' })}
                          name="result_of_job_fair_other"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          classes={{ root: styles.textFieldRoot }}
                          type="text"
                          value={values.result_of_job_fair_other}
                          variant="filled"
                          inputProps={{
                            title: intl.formatMessage({ id: 'jobSearchActivity.fair.resultOption4' }),
                          }}
                          id="other-job-fair-work-search-field"
                        />
                      </Box>
                    ) : null}
                    <div style={{ position: 'relative', top: '-27px', left: '-26px' }}>
                      {touched.result_of_job_fair && errors.result_of_job_fair && (
                        <FormHelperText classes={{ root: onbStyles.tosHelperTextRoot }}>
                          {errors.result_of_job_fair}
                        </FormHelperText>
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.boxes}>
                  <div>
                    <Box pt={2} pb={1}>
                      <span className={styles.fieldLabel}>
                        {intl.formatMessage({ id: 'jobSearchActivity.form.uploadDocuments' })}
                      </span>
                    </Box>
                    <Field
                      name="upload_documents"
                      disabled={submitting || (editting && !editMode)}
                      component={BrowseDocuments}
                      label="jobSearchActivity.fair.documentHint"
                      buttonId="browse-documents-job-fair-work-search-button"
                    />
                    <div className={styles.downloadLinks}>
                      {documentsLinks.map((doc) =>
                        doc.downloadUrl ? (
                          <a key={doc.downloadUrl} href={doc.downloadUrl}>
                            {doc.filename}
                          </a>
                        ) : (
                          <span key={JSON.stringify(doc)}>...</span>
                        )
                      )}
                    </div>
                  </div>
                  <div>
                    <Box pt={2} pb={1}>
                      <span className={styles.fieldLabel}>
                        {intl.formatMessage({ id: 'jobSearchActivity.form.notes' })}
                      </span>
                    </Box>
                    <TextField
                      id="notes-job-fair-work-search-field"
                      disabled={submitting || (editting && !editMode)}
                      placeholder={intl.formatMessage({ id: 'jobSearchActivity.form.notes' })}
                      multiline
                      rows={5}
                      variant="outlined"
                      InputProps={{ style: { fontSize: 12, height: '112px' } }}
                      value={values.notes}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      name="notes"
                      inputProps={{
                        title: intl.formatMessage({ id: 'jobSearchActivity.form.notes' }),
                      }}
                    />
                  </div>
                </div>
              </>
            )}
            <Box display="flex" justifyContent="space-between" mt={4}>
              {saveError ? (
                <Typography color="error" variant="body2" sx={{ alignSelf: 'center' }}>
                  {intl.formatMessage({ id: 'app.error' })}
                </Typography>
              ) : (
                <div />
              )}
              <Box display="flex">
                <Box mr={1}>
                  <Button
                    type="button"
                    disabled={submitting}
                    size="large"
                    onClick={() => setOpen(false)}
                    variant="outlined"
                    color="secondary"
                    id="cancel-job-fair-work-search-button"
                  >
                    {submitting ? (
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">{intl.formatMessage({ id: 'jobSearchActivity.form.cancel' })}</span>
                      </div>
                    ) : (
                      intl.formatMessage({ id: 'jobSearchActivity.form.cancel' })
                    )}
                  </Button>
                </Box>
                {editting && !editMode ? (
                  <Button
                    color="secondary"
                    disabled={submitting || !selectedActivity?.draft}
                    size="large"
                    variant="contained"
                    onClick={(evt) => {
                      evt.preventDefault();
                      trackEvent('WEEK_VIEWACTIVITY_EDIT');
                      setEditMode(true);
                    }}
                    id="edit-job-fair-work-search-button"
                  >
                    {intl.formatMessage({ id: 'layout.header.edit' })}
                  </Button>
                ) : (
                  <Button
                    color="secondary"
                    disabled={submitting || Boolean(dateError) || (editting && !editMode)}
                    size="large"
                    type="submit"
                    variant="contained"
                    id="save-job-fair-work-search-button"
                  >
                    {submitting ? (
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">{intl.formatMessage({ id: 'jobSearchActivity.form.save' })}</span>
                      </div>
                    ) : (
                      intl.formatMessage({ id: 'jobSearchActivity.form.save' })
                    )}
                  </Button>
                )}
              </Box>
            </Box>
          </form>
        );
      }}
    </Formik>
  );
}

AttendJobSeminar.propTypes = {
  setOpen: PropTypes.func,
  selectedActivity: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  editting: PropTypes.bool,
  week: PropTypes.object,
  onSubmit: PropTypes.func,
};

export default AttendJobSeminar;
